import React, { useState } from 'react'
import { PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Collapse, Flex, Form, Input, List, Select, Tag, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAppConnections, useAppTools } from '../hooks'
import { AppTool, ToolSelection } from '../types'
import { actionKey, maxActions } from '../tool-configuration'
import { policyApproved } from '../action-policy'
import { AppPicker } from './app-picker'
import { useDebouncedSearch } from './app-connections'
import { AppIcon } from './app-icon'
import { LoadMoreOnScroll } from './load-more-on-scroll'
import { ActionInfo } from './action-info'
import { CapabilityReview } from './capability-review'
import { ConfigurationDialog } from './configuration-dialog'
import { useAuth } from '../../auth'
import { INDEX_APP_CONNECTION, STORE_APP_CONNECTION } from '../../user/permission'

type Configuring = {
    connectionIds: string[]
    autoStart: boolean
}

export function ToolEditor({ creation = false }: { creation?: boolean }) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const form = Form.useFormInstance()
    const [invalid, setInvalid] = useState(false)
    const [applied, setApplied] = useState(false)
    const [configurationRevision, setConfigurationRevision] = useState(0)
    const [picking, setPicking] = useState(false)
    const [configuring, setConfiguring] = useState<Configuring>()

    // Watch app membership only. Fixed-input edits do not rerender this card.
    const usedApps =
        Form.useWatch(
            (values) => [...new Set<string>((values.tools ?? []).map((tool: ToolSelection) => tool.connectionId))],
            {
                form,
                preserve: true,
            }
        ) ?? []

    const validateTools = async (_: unknown, value: ToolSelection[] = []) => {
        if (value.some((tool) => !policyApproved(tool))) {
            throw new Error(t('Review and approve the access settings for each action'))
        }

        if (invalid) {
            throw new Error(t('Enter valid JSON'))
        }
    }

    const configureNewApps = (ids: string[]) => {
        setPicking(false)
        setConfiguring({
            connectionIds: [...usedApps, ...ids],
            autoStart: true,
        })
    }

    const applyConfiguration = (next: ToolSelection[]) => {
        setApplied(true)
        setInvalid(false)
        setConfigurationRevision((revision) => revision + 1)
        form.setFieldValue('tools', next)
    }

    if (!can(INDEX_APP_CONNECTION)) {
        return null
    }

    return (
        <Card
            title={
                <Flex
                    align="center"
                    gap="small"
                >
                    {t('What can Evaa do with your apps?')}
                    <ActionInfo name={t('App actions')}>
                        {t(
                            'Choose the apps Evaa can use. She helps you set them up and asks you to review access before using them.'
                        )}
                    </ActionInfo>
                </Flex>
            }
            className="mb-6"
        >
            <Flex
                vertical
                gap="middle"
            >
                {usedApps.length === 0 && (
                    <Typography.Text type="secondary">
                        {t(
                            'Let Evaa use your apps during the conversation, for example to check a calendar or create a record.'
                        )}
                    </Typography.Text>
                )}
                {applied && (
                    <Alert
                        type="success"
                        showIcon
                        message={
                            creation
                                ? t('Configuration ready. You can try it in the Test conversation step.')
                                : t('Configuration ready. Save your settings, then open Test conversation to try it.')
                        }
                    />
                )}
                <Form.Item
                    name="tools"
                    // Stay mounted while empty so the form keeps the field.
                    hidden={!usedApps.length}
                    className="!mb-0"
                    // JSON controls report validity locally; navigation and save validate this field.
                    validateTrigger={[]}
                    rules={[{ validator: validateTools }]}
                >
                    <SelectedActions
                        key={configurationRevision}
                        onValidityChange={setInvalid}
                    />
                </Form.Item>
                {can(STORE_APP_CONNECTION) && (
                    <Flex
                        gap="small"
                        wrap
                    >
                        <Button
                            icon={<PlusOutlined />}
                            type={usedApps.length ? 'default' : 'primary'}
                            onClick={() => setPicking(true)}
                        >
                            {t('Add app')}
                        </Button>
                        {usedApps.length > 0 && (
                            <Tooltip title={invalid ? t('Enter valid JSON') : undefined}>
                                <Button
                                    disabled={invalid}
                                    onClick={() =>
                                        setConfiguring({
                                            connectionIds: usedApps,
                                            autoStart: false,
                                        })
                                    }
                                >
                                    {t('Adjust with Evaa')}
                                </Button>
                            </Tooltip>
                        )}
                    </Flex>
                )}
                <Collapse
                    size="small"
                    items={[
                        {
                            key: 'manual',
                            label: t('Advanced: choose individual actions'),
                            children: <AvailableActions />,
                        },
                    ]}
                />
            </Flex>
            {picking && (
                <AppPicker
                    usedConnectionIds={usedApps}
                    onCancel={() => setPicking(false)}
                    onConfirm={configureNewApps}
                />
            )}
            {configuring && (
                <ConfigurationDialog
                    connectionIds={configuring.connectionIds}
                    autoStart={configuring.autoStart}
                    invalid={invalid}
                    onClose={() => setConfiguring(undefined)}
                    onApply={applyConfiguration}
                />
            )}
        </Card>
    )
}

// Action membership affects the catalog buttons; editing fixed values does not.
function AvailableActions() {
    const { t } = useTranslation()
    const form = Form.useFormInstance()
    const selectedKeys =
        Form.useWatch((values) => (values.tools ?? []).map((tool: ToolSelection) => actionKey(tool)), {
            form,
            preserve: true,
        }) ?? []
    const connections = useAppConnections()
    const [connectionId, setConnectionId] = useState<string>()
    const [searchText, setSearchText] = useState('')
    const search = useDebouncedSearch(searchText)
    const tools = useAppTools(connectionId, search)
    const appName = connections.data?.find((item) => item.id === connectionId)?.name

    const connectedOptions = connections.data
        ?.filter((connection) => connection.status === 'connected')
        .map((connection) => ({
            label: (
                <Flex
                    align="center"
                    gap="small"
                >
                    <AppIcon
                        name={connection.name}
                        toolkit={connection.toolkit}
                        size={20}
                    />
                    {connection.name}
                </Flex>
            ),
            value: connection.id,
        }))

    const isAddable = (key: string) => selectedKeys.length < maxActions && !selectedKeys.includes(key)

    const chooseConnection = (value: string) => {
        setConnectionId(value)
        setSearchText('')
    }

    const addTool = (tool: AppTool) => {
        if (!connectionId) {
            return
        }

        const selections: ToolSelection[] = form.getFieldValue('tools') ?? []
        const key = actionKey({ ...tool, connectionId })

        if (selections.length >= maxActions || selections.some((item) => actionKey(item) === key)) {
            return
        }

        form.setFieldValue('tools', [
            ...selections,
            {
                ...tool,
                connectionId,
                fixedArgumentsJson: '{}',
            },
        ])
    }

    return (
        <>
            <Flex
                vertical
                gap="middle"
                className="mb-4"
            >
                {connections.isError && (
                    <Alert
                        type="error"
                        message={t('Could not load connected apps')}
                    />
                )}
                <Flex
                    gap="small"
                    wrap
                >
                    <Select
                        className="min-w-64"
                        placeholder={t('Choose connected app')}
                        loading={connections.isPending}
                        value={connectionId}
                        onChange={chooseConnection}
                        options={connectedOptions}
                    />
                </Flex>
                {connectionId && (
                    <>
                        <Input.Search
                            placeholder={t('Search actions')}
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') event.preventDefault()
                            }}
                            allowClear
                        />
                        {tools.isError && (
                            <Alert
                                type="error"
                                message={t('Could not load app actions')}
                            />
                        )}
                        <List
                            className="max-h-80 overflow-y-auto"
                            size="small"
                            loading={tools.isPending}
                            loadMore={
                                <LoadMoreOnScroll
                                    hasMore={!!tools.hasNextPage}
                                    loading={tools.isFetchingNextPage}
                                    onLoad={() => void tools.fetchNextPage()}
                                />
                            }
                            dataSource={tools.data?.pages.flatMap((page) => page.items) ?? []}
                            renderItem={(tool) => (
                                <CatalogActionItem
                                    tool={tool}
                                    appName={appName ?? tool.toolkit}
                                    addable={isAddable(actionKey({ ...tool, connectionId }))}
                                    onAdd={() => addTool(tool)}
                                />
                            )}
                        />
                    </>
                )}
            </Flex>
        </>
    )
}

type CatalogActionItemProps = {
    tool: AppTool
    appName: string
    addable: boolean
    onAdd: () => void
}

function CatalogActionItem({ tool, appName, addable, onAdd }: CatalogActionItemProps) {
    const { t } = useTranslation()

    return (
        <List.Item>
            <Flex
                align="center"
                justify="space-between"
                gap="small"
                className="w-full"
            >
                <Flex
                    vertical
                    gap={4}
                    className="min-w-0 flex-1"
                >
                    <Flex
                        align="center"
                        gap="small"
                    >
                        <AppIcon
                            name={appName}
                            toolkit={tool.toolkit}
                            size={20}
                        />
                        <Typography.Text className="min-w-0 break-words">{tool.name}</Typography.Text>
                    </Flex>
                    <Tag
                        className="!m-0 self-start"
                        color={tool.readOnly ? 'blue' : 'orange'}
                    >
                        {tool.readOnly ? t('Reads data') : t('Changes data')}
                    </Tag>
                </Flex>
                <Flex
                    align="center"
                    gap="small"
                    className="shrink-0"
                >
                    <ActionInfo name={tool.name}>{tool.description}</ActionInfo>
                    <Button
                        size="small"
                        disabled={!addable}
                        onClick={onAdd}
                    >
                        {t('Add')}
                    </Button>
                </Flex>
            </Flex>
        </List.Item>
    )
}

type SelectedActionsProps = {
    value?: ToolSelection[]
    onChange?: (value: ToolSelection[]) => void
    onValidityChange: (invalid: boolean) => void
}

function SelectedActions({ value: selections = [], onChange, onValidityChange }: SelectedActionsProps) {
    const connections = useAppConnections()
    const [invalidInputs, setInvalidInputs] = useState<Record<string, boolean>>({})

    const changeSelections = (next: ToolSelection[]) => {
        onChange?.(next)
        setInvalidInputs(
            Object.fromEntries(next.map((tool) => [actionKey(tool), invalidInputs[actionKey(tool)] ?? false]))
        )
        onValidityChange(next.some((tool) => invalidInputs[actionKey(tool)]))
    }

    const markInvalid = (key: string, invalid: boolean) => {
        const next = { ...invalidInputs, [key]: invalid }

        setInvalidInputs(next)
        onValidityChange(selections.some((tool) => next[actionKey(tool)]))
    }

    return (
        <CapabilityReview
            tools={selections}
            connections={connections.data ?? []}
            invalidKeys={invalidInputs}
            onChange={changeSelections}
            onValidityChange={markInvalid}
        />
    )
}
